import {
  CustomBodyCell,
  CustomHeaderCell,
  CustomTable,
  CustomTableContainer,
  CustomTableRow,
} from '@components/Styles/TableStyle';
import { CustomStack, SectionWrapper } from '@components/Styles/CommonStyle';
import { TableBody, TableHead, Typography } from '@mui/material';
import { formatDate, formatDateRange } from '@utils/formatter';

import { CustomButton } from '@components/Styles/ButtonStyle';
import { PROFILE_EDUCATION_STATUS_CONVERTER } from '@constants/profile';
import React from 'react';
import { saveAsDocx } from '@containers/profile/ExportToDocx';
import { theme } from '@components/Styles/theme';

const ProfileViewTable = ({ data, onPrint }) => {
  const { profileData, educationList, workExperienceList, projectHistoryList } = data;

  return (
    <React.Fragment>
      <CustomStack justifyContent="spaceBetween" mb={2}>
        <Typography sx={theme.typography.h4}>프로필 정보</Typography>
        <CustomStack>
          <CustomButton variant="outlined" onClick={() => onPrint()}>
            인쇄
          </CustomButton>
          <CustomButton
            onClick={(e) => {
              e.preventDefault();
              saveAsDocx(data);
            }}
          >
            저장(doc)
          </CustomButton>
        </CustomStack>
      </CustomStack>
      <SectionWrapper>
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>정보</Typography>
        </CustomStack>
        <CustomTableContainer>
          <CustomTable sixColumn>
            <TableBody>
              <CustomTableRow>
                <CustomHeaderCell>성명</CustomHeaderCell>
                <CustomBodyCell>{profileData.name}</CustomBodyCell>
                <CustomHeaderCell>생년월일</CustomHeaderCell>
                <CustomBodyCell>{profileData.formattedBirthDate || '-'}</CustomBodyCell>
                <CustomHeaderCell>성별</CustomHeaderCell>
                <CustomBodyCell>{profileData.convertedGender}</CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>경력</CustomHeaderCell>
                <CustomBodyCell>{profileData.workPeriod}</CustomBodyCell>
                <CustomHeaderCell>등급</CustomHeaderCell>
                <CustomBodyCell>{profileData.convertedGrade}</CustomBodyCell>
                <CustomHeaderCell>근무구분</CustomHeaderCell>
                <CustomBodyCell>{profileData.convertedWorkType}</CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>휴대폰</CustomHeaderCell>
                <CustomBodyCell>{profileData.formattedPhone}</CustomBodyCell>
                <CustomHeaderCell>이메일</CustomHeaderCell>
                <CustomBodyCell>{profileData.email}</CustomBodyCell>
                <CustomHeaderCell>직위</CustomHeaderCell>
                <CustomBodyCell>{profileData.position}</CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>주소</CustomHeaderCell>
                <CustomBodyCell colSpan={5}>{profileData.address || '-'}</CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>자격증</CustomHeaderCell>
                <CustomBodyCell colSpan={2}>{profileData.license || '없음'}</CustomBodyCell>
                <CustomHeaderCell>취득년월</CustomHeaderCell>
                <CustomBodyCell colSpan={2}>{profileData.formattedAcquisitionDate || '-'}</CustomBodyCell>
              </CustomTableRow>
            </TableBody>
          </CustomTable>
        </CustomTableContainer>
      </SectionWrapper>

      <SectionWrapper>
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>학력</Typography>
        </CustomStack>

        <CustomTableContainer>
          <CustomTable>
            <TableHead>
              <CustomTableRow>
                <CustomHeaderCell align="center">학교</CustomHeaderCell>
                <CustomHeaderCell align="center">전공</CustomHeaderCell>
                <CustomHeaderCell align="center">졸업여부</CustomHeaderCell>
                <CustomHeaderCell align="center">졸업일</CustomHeaderCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {educationList.length > 0 ? (
                educationList.map((education, index) => {
                  education.convertedStatus = PROFILE_EDUCATION_STATUS_CONVERTER[education.educationStatus] || '-';
                  education.formattedGraduationDate =
                    (education.graduationYear ? `${education.graduationYear}년 ` : '') +
                    (education.graduationMonth ? `${education.graduationMonth}월` : '');

                  return (
                    <CustomTableRow key={index}>
                      <CustomBodyCell align="center">{education.schoolName}</CustomBodyCell>
                      <CustomBodyCell align="center">{education.major}</CustomBodyCell>
                      <CustomBodyCell align="center">{education.convertedStatus}</CustomBodyCell>
                      <CustomBodyCell align="center">{education.formattedGraduationDate || '-'}</CustomBodyCell>
                    </CustomTableRow>
                  );
                })
              ) : (
                <CustomTableRow>
                  <CustomBodyCell colSpan={4} align="center">
                    등록된 학력 정보가 없습니다.
                  </CustomBodyCell>
                </CustomTableRow>
              )}
            </TableBody>
          </CustomTable>
        </CustomTableContainer>
      </SectionWrapper>

      <SectionWrapper>
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>근무경력</Typography>
        </CustomStack>
        <CustomTableContainer>
          <CustomTable>
            <TableHead>
              <CustomTableRow>
                <CustomHeaderCell align="center">회사명</CustomHeaderCell>
                <CustomHeaderCell align="center">근무기간</CustomHeaderCell>
                <CustomHeaderCell align="center">직위</CustomHeaderCell>
                <CustomHeaderCell align="center">담당업무</CustomHeaderCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {workExperienceList.length > 0 ? (
                workExperienceList.map((workExperience, index) => {
                  workExperience.formattedWorkPeriod = formatDateRange(
                    workExperience.startYear,
                    workExperience.startMonth,
                    workExperience.endYear,
                    workExperience.endMonth,
                  );
                  workExperience.startDate = formatDate(workExperience.startYear, workExperience.startMonth);
                  workExperience.endDate = formatDate(workExperience.endYear, workExperience.endMonth);
                  return (
                    <CustomTableRow key={index}>
                      <CustomBodyCell align="center">{workExperience.companyName}</CustomBodyCell>
                      <CustomBodyCell align="center">{workExperience.formattedWorkPeriod}</CustomBodyCell>
                      <CustomBodyCell align="center">{workExperience.position}</CustomBodyCell>
                      <CustomBodyCell align="center">{workExperience.duty}</CustomBodyCell>
                    </CustomTableRow>
                  );
                })
              ) : (
                <CustomTableRow>
                  <CustomBodyCell colSpan={4} align="center">
                    등록된 근무경력이 없습니다.
                  </CustomBodyCell>
                </CustomTableRow>
              )}
            </TableBody>
          </CustomTable>
        </CustomTableContainer>
      </SectionWrapper>

      <SectionWrapper className="page-break">
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>기술경력</Typography>
        </CustomStack>
        <CustomTableContainer>
          <CustomTable>
            <TableHead>
              <CustomTableRow>
                <CustomHeaderCell align="center">사업명</CustomHeaderCell>
                <CustomHeaderCell align="center">참여기간</CustomHeaderCell>
                <CustomHeaderCell align="center">고객사</CustomHeaderCell>
                <CustomHeaderCell align="center">근무회사</CustomHeaderCell>
                <CustomHeaderCell align="center">역할</CustomHeaderCell>
                <CustomHeaderCell align="center">사용기술</CustomHeaderCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {projectHistoryList.length > 0 ? (
                projectHistoryList.map((projectHistory, index) => {
                  projectHistory.formattedWorkPeriod = formatDateRange(
                    projectHistory.startYear,
                    projectHistory.startMonth,
                    projectHistory.endYear,
                    projectHistory.endMonth,
                  );
                  projectHistory.startDate = formatDate(projectHistory.startYear, projectHistory.startMonth);
                  projectHistory.endDate = formatDate(projectHistory.endYear, projectHistory.endMonth);
                  return (
                    <CustomTableRow key={index}>
                      <CustomBodyCell>{projectHistory.projectName}</CustomBodyCell>
                      <CustomBodyCell align="center">{projectHistory.formattedWorkPeriod}</CustomBodyCell>
                      <CustomBodyCell align="center">{projectHistory.clientCompany}</CustomBodyCell>
                      <CustomBodyCell align="center">{projectHistory.employerCompany}</CustomBodyCell>
                      <CustomBodyCell align="center">{projectHistory.duty}</CustomBodyCell>
                      <CustomBodyCell align="center">{projectHistory.skill}</CustomBodyCell>
                    </CustomTableRow>
                  );
                })
              ) : (
                <CustomTableRow>
                  <CustomBodyCell colSpan={6} align="center">
                    등록된 기술경력이 없습니다.
                  </CustomBodyCell>
                </CustomTableRow>
              )}
            </TableBody>
          </CustomTable>
        </CustomTableContainer>
      </SectionWrapper>
    </React.Fragment>
  );
};

export default ProfileViewTable;
